import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  LIBRARY_CATEGORIES,
  libraryApi,
  type LibraryDocument,
  type LibraryOptions,
} from '../api/library'
import {
  FormGrid,
  Button, Callout, Card, Checkbox, Dialog as ModalDialog, EmptyState, FilterBar, Icon, LoadingState, Page, PageHeader, Select,
  StatusPill, Tabs, TextField, useConfirm,
} from '../components/ui'
import UserDepartmentsPanel from '../components/UserDepartmentsPanel'
import { useFeatureFlag } from '../lib/featureFlags'

/**
 * F-138: la Libreria, documenti condivisi (presentazioni, documenti utili, listini prezzi). Aperta a ogni utente
 * autenticato: il server restituisce solo i documenti validi oggi e visibili per ruolo e ramo, quindi qui non c'e'
 * nessun filtro di sicurezza lato client. I comandi di gestione (carica, modifica, nuova versione, ritira, mostra
 * scaduti) compaiono solo se `options.canManage` e, in ogni caso, il server risponde 403 a chi non gestisce.
 */

const EXPIRING_SOON_DAYS = 30

// Link di download dall'API (non una rotta): nessun componente ui per <a> a forma di bottone, si riusano le classi dei bottoni ui.
const ANCHOR_BUTTON = 'md-button md-button--outlined md-tone--primary md-button--sm md-state md-touch'

function parseDay(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d)
}

function formatDay(iso: string): string {
  return parseDay(iso).toLocaleDateString('it-IT')
}

function daysFromToday(iso: string): number {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return Math.round((parseDay(iso).getTime() - today.getTime()) / 86_400_000)
}

function todayIso(): string {
  const t = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}`
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function ValidityBadges({ doc }: { doc: LibraryDocument }) {
  if (doc.status === 'NonAncoraValido')
    return <StatusPill tone="info">Valido dal {formatDay(doc.validFrom)}</StatusPill>
  if (doc.status === 'Scaduto')
    return <StatusPill tone="error">Scaduto il {doc.validTo ? formatDay(doc.validTo) : ''}</StatusPill>
  if (!doc.validTo) return null
  const left = daysFromToday(doc.validTo)
  return (
    <StatusPill tone={left <= EXPIRING_SOON_DAYS ? 'warning' : 'neutral'}>
      Scade il {formatDay(doc.validTo)}
    </StatusPill>
  )
}

interface FormState {
  title: string
  category: string
  description: string
  validFrom: string
  validTo: string
  roles: string[]
  departmentIds: number[]
  file: File | null
}

const EMPTY_FORM: FormState = {
  title: '',
  category: LIBRARY_CATEGORIES[0],
  description: '',
  validFrom: todayIso(),
  validTo: '',
  roles: [],
  departmentIds: [],
  file: null,
}

function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter(v => v !== value) : [...list, value]
}

type Dialog =
  | { kind: 'upload' }
  | { kind: 'edit'; doc: LibraryDocument }
  | { kind: 'version'; doc: LibraryDocument }
  | null

export function LibraryPage() {
  const confirm = useConfirm()
  const [options, setOptions] = useState<LibraryOptions | null>(null)
  const [items, setItems] = useState<LibraryDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [category, setCategory] = useState<string>('')
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [includeExpired, setIncludeExpired] = useState(false)
  const [dialog, setDialog] = useState<Dialog>(null)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [dialogError, setDialogError] = useState('')

  const canManage = options?.canManage ?? false
  const departmentsOn = useFeatureFlag('libreria.rami_utenti')

  useEffect(() => {
    libraryApi.options().then(setOptions).catch(e => setError(e instanceof Error ? e.message : 'Errore'))
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setItems(await libraryApi.list({ q: query, includeExpired: includeExpired && canManage }))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setLoading(false)
    }
  }, [query, includeExpired, canManage])

  useEffect(() => {
    void load()
  }, [load])

  const counts = useMemo(() => {
    const map: Record<string, number> = {}
    for (const d of items) map[d.category] = (map[d.category] ?? 0) + 1
    return map
  }, [items])

  const shown = category ? items.filter(d => d.category === category) : items
  const departmentName = (id: number) => options?.departments.find(d => d.id === id)?.name ?? `#${id}`

  function openUpload() {
    setForm({ ...EMPTY_FORM, category: category || LIBRARY_CATEGORIES[0], validFrom: todayIso() })
    setDialogError('')
    setDialog({ kind: 'upload' })
  }

  function openEdit(doc: LibraryDocument) {
    setForm({
      title: doc.title,
      category: doc.category,
      description: doc.description ?? '',
      validFrom: doc.validFrom,
      validTo: doc.validTo ?? '',
      roles: doc.roles ?? [],
      departmentIds: doc.departmentIds ?? [],
      file: null,
    })
    setDialogError('')
    setDialog({ kind: 'edit', doc })
  }

  function openVersion(doc: LibraryDocument) {
    setForm({ ...EMPTY_FORM, title: doc.title, category: doc.category, description: doc.description ?? '', validFrom: todayIso() })
    setDialogError('')
    setDialog({ kind: 'version', doc })
  }

  async function save() {
    if (!dialog) return
    setSaving(true)
    setDialogError('')
    try {
      if (dialog.kind === 'upload') {
        if (!form.file) throw new Error('Scegli il file da caricare.')
        await libraryApi.upload({
          title: form.title, category: form.category, description: form.description,
          validFrom: form.validFrom, validTo: form.validTo, roles: form.roles, departmentIds: form.departmentIds,
        }, form.file)
      } else if (dialog.kind === 'edit') {
        await libraryApi.update(dialog.doc.id, {
          title: form.title, category: form.category, description: form.description.trim() || null,
          validFrom: form.validFrom || null, validTo: form.validTo || null,
          roles: form.roles, departmentIds: form.departmentIds,
        })
      } else {
        if (!form.file) throw new Error('Scegli il file della nuova versione.')
        await libraryApi.newVersion(dialog.doc.id, {
          title: form.title, description: form.description, validFrom: form.validFrom, validTo: form.validTo,
        }, form.file)
      }
      setDialog(null)
      await load()
    } catch (e) {
      setDialogError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setSaving(false)
    }
  }

  async function retire(doc: LibraryDocument) {
    const ok = await confirm({
      title: 'Ritirare il documento',
      message: `Ritirare "${doc.title}"? Sparirà dalla libreria per tutti; il file resta archiviato.`,
      confirmLabel: 'Ritira',
      destructive: true,
    })
    if (!ok) return
    setError('')
    try {
      await libraryApi.retire(doc.id)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante il ritiro')
    }
  }

  const dialogTitle =
    dialog?.kind === 'upload' ? 'Carica documento'
      : dialog?.kind === 'edit' ? 'Modifica documento'
        : dialog?.kind === 'version' ? `Nuova versione di "${dialog.doc.title}"` : ''

  return (
    <Page>
      <PageHeader
        title="Libreria"
        icon="library_books"
        actions={
          canManage ? (
            <Button variant="filled" icon="upload_file" onClick={openUpload}>
              Carica documento
            </Button>
          ) : undefined
        }
      />

      <FilterBar onSubmit={() => setQuery(search)}>
        <TextField type="search" label="Cerca per titolo, descrizione o nome file" aria-label="Cerca nella libreria"
          value={search} onChange={e => setSearch(e.target.value)} />
        {canManage && (
          <Checkbox id="library-expired" label="Mostra scaduti" checked={includeExpired}
            onChange={e => setIncludeExpired(e.target.checked)} />
        )}
      </FilterBar>

      {departmentsOn && canManage && <UserDepartmentsPanel />}

      <Tabs
        aria-label="Categorie libreria"
        value={category}
        onChange={setCategory}
        items={[
          { id: '', label: 'Tutti', badge: items.length },
          ...LIBRARY_CATEGORIES.map(c => ({ id: c, label: c, badge: counts[c] ?? 0 })),
        ]}
      />

      {error && <Callout tone="error">{error}</Callout>}

      <Card variant="outlined" padding="none">
        {loading ? (
          <LoadingState />
        ) : shown.length === 0 ? (
          <EmptyState icon="folder_open" title="Nessun documento in libreria" />
        ) : (
          <ul className="list-unstyled mb-0">
            {shown.map(d => (
              <li key={d.id} className="p-3 border-bottom">
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                  <div>
                    <div className="md-typescale-emphasized-body-medium">
                      {d.title}
                      {!category && <StatusPill tone="neutral" className="ms-2">{d.category}</StatusPill>}
                      <span className="ms-2"><ValidityBadges doc={d} /></span>
                      {canManage && !d.isLatestVersion && <StatusPill tone="neutral" className="ms-2">Versione sostituita</StatusPill>}
                    </div>
                    {d.description && <div className="md-typescale-body-small mt-1">{d.description}</div>}
                    <div className="text-muted md-typescale-body-small mt-1">
                      {d.originalFileName} · {formatBytes(d.sizeBytes)} · valido dal {formatDay(d.validFrom)}
                    </div>
                    {canManage && (
                      <div className="text-muted md-typescale-body-small">
                        Visibile a: {d.roles && d.roles.length > 0 ? d.roles.join(', ') : 'tutti i ruoli'}
                        {' · '}
                        {d.departmentIds && d.departmentIds.length > 0
                          ? d.departmentIds.map(departmentName).join(', ') : 'tutti i rami'}
                      </div>
                    )}
                  </div>
                  <div className="d-flex gap-2">
                    <a href={libraryApi.contentUrl(d.id)} className={ANCHOR_BUTTON} download={d.originalFileName}>
                      <Icon name="download" size={18} />
                      Scarica
                    </a>
                    {canManage && (
                      <>
                        <Button variant="outlined" size="sm" onClick={() => openEdit(d)}>Modifica</Button>
                        {d.isLatestVersion && (
                          <Button variant="outlined" size="sm" onClick={() => openVersion(d)}>
                            Nuova versione
                          </Button>
                        )}
                        <Button variant="outlined" size="sm" onClick={() => void retire(d)}>Ritira</Button>
                      </>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <ModalDialog
        as="form"
        onSubmit={(e) => { e.preventDefault(); void save() }}
        open={Boolean(dialog && options)}
        onClose={() => setDialog(null)}
        dismissible={!saving}
        title={dialogTitle}
        actions={
          <>
            <Button variant="text" onClick={() => setDialog(null)} disabled={saving}>
              Annulla
            </Button>
            <Button type="submit" variant="text" disabled={saving}>
              {saving ? 'Salvataggio…' : 'Salva'}
            </Button>
          </>
        }
      >
        {dialog && options && (
          <>
            {dialogError && <Callout tone="error">{dialogError}</Callout>}
            <FormGrid columns={3}>
              <div className="md-span-2">
                <TextField id="lib-title" label="Titolo" maxLength={200} value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
              </div>
              <div>
                <Select id="lib-cat" label="Categoria" value={form.category}
                  disabled={dialog.kind === 'version'}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                  {LIBRARY_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </Select>
              </div>
              <div className="md-span-full">
                <TextField id="lib-desc" label="Descrizione breve" multiline rows={2} maxLength={1000} value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
              </div>
              <div className="md-span-2">
                <TextField id="lib-from" type="date" label="Valido dal" value={form.validFrom}
                  {...(dialog.kind === 'version'
                    ? { supportingText: 'La versione precedente resta valida fino al giorno prima di questa data.' }
                    : {})}
                  onChange={e => setForm(f => ({ ...f, validFrom: e.target.value }))} />
              </div>
              <div className="md-span-2">
                <TextField id="lib-to" type="date" label="Valido fino al (facoltativo)" value={form.validTo}
                  onChange={e => setForm(f => ({ ...f, validTo: e.target.value }))} />
              </div>

              {dialog.kind !== 'version' && (
                <>
                  <div className="md-span-2">
                    <div className="md-typescale-emphasized-body-small">Ruoli che lo vedono</div>
                    <div className="text-muted md-typescale-body-small mb-1">Nessuna scelta = tutti i ruoli.</div>
                    <div className="overflow-auto md-u-scroll-y-180">
                      {options.roles.map(r => (
                        <Checkbox key={r} id={`lib-role-${r}`} label={r}
                          checked={form.roles.includes(r)}
                          onChange={() => setForm(f => ({ ...f, roles: toggle(f.roles, r) }))} />
                      ))}
                    </div>
                  </div>
                  <div className="md-span-2">
                    <div className="md-typescale-emphasized-body-small">Rami che lo vedono</div>
                    <div className="text-muted md-typescale-body-small mb-1">Nessuna scelta = tutti i rami.</div>
                    <div className="overflow-auto md-u-scroll-y-180">
                      {options.departments.map(d => (
                        <Checkbox key={d.id} id={`lib-dep-${d.id}`} label={d.name}
                          checked={form.departmentIds.includes(d.id)}
                          onChange={() => setForm(f => ({ ...f, departmentIds: toggle(f.departmentIds, d.id) }))} />
                      ))}
                    </div>
                  </div>
                </>
              )}

              {dialog.kind !== 'edit' && (
                <div className="md-span-full">
                  <TextField id="lib-file" type="file" label="File"
                    accept={options.allowedExtensions.map(x => `.${x}`).join(',')}
                    supportingText={`Formati ammessi: ${options.allowedExtensions.join(', ')}. Massimo ${Math.round(options.maxBytes / (1024 * 1024))} MB.`}
                    onChange={e => setForm(f => ({ ...f, file: e.target.files?.[0] ?? null }))} />
                </div>
              )}
            </FormGrid>
          </>
        )}
      </ModalDialog>
    </Page>
  )
}
